import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lifebuoy, Bug, Receipt, Lightbulb, ChatCircleText, Envelope, PaperPlaneTilt, CheckCircle, Spinner } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const CATEGORIES = [
  { key: "bug", label: "Bug", icon: Bug, color: "text-red-400" },
  { key: "billing", label: "Billing", icon: Receipt, color: "text-emerald-400" },
  { key: "feature", label: "Feature Request", icon: Lightbulb, color: "text-amber-400" },
  { key: "general", label: "General", icon: ChatCircleText, color: "text-orange-400" },
];

const FAQS = [
  {
    q: "How do I cancel my subscription?",
    a: "Go to your Profile page. If you're on Pro or Agency, you'll see a 'Cancel subscription' button. You keep access until the end of your billing period, then auto-downgrade to Free.",
  },
  {
    q: "How do I reset my password?",
    a: "Log out, click 'Forgot password?' on the login page, enter your email — we'll email you a reset link that expires in 1 hour.",
  },
  {
    q: "Where are my leads coming from?",
    a: "Real businesses from OpenStreetMap — the same public database powering Apple Maps and countless SaaS tools. No fake data, ever.",
  },
  {
    q: "Why is Hunter showing 'no email found' for some leads?",
    a: "Hunter can only find emails that are publicly published on a business's website. If they don't list an email anywhere, Hunter (and every other tool) won't find one. Try their contact page manually.",
  },
  {
    q: "How do I use the free trial?",
    a: "Every new account gets 14 days of full Pro access — no credit card required. When the trial ends, you automatically drop to Free tier with feature limits.",
  },
];

export default function Support() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState("general");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { ticket_id, message }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3) return toast.error("Subject must be at least 3 characters");
    if (message.trim().length < 10) return toast.error("Message must be at least 10 characters");
    setLoading(true);
    try {
      const { data } = await api.post("/support/ticket", {
        category,
        subject: subject.trim(),
        message: message.trim(),
        name: name.trim() || undefined,
        email: email.trim() || undefined,
      });
      setSubmitted({ ticket_id: data.ticket_id, message: data.message });
      setSubject("");
      setMessage("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to send");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <PageHeader
        title="Support"
        subtitle="Real humans, real replies. We answer every ticket within 24 hours."
        icon={Lifebuoy}
      />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left — Ticket form or success */}
        <div className="lg:col-span-3">
          {submitted ? (
            <div
              data-testid="support-success"
              className="bg-emerald-500/[0.06] border border-emerald-500/30 rounded-sm p-8 text-center"
            >
              <CheckCircle size={48} weight="fill" className="text-emerald-400 mx-auto mb-4" />
              <div className="text-lg font-display font-semibold text-emerald-100 mb-2">Message received</div>
              <div className="text-sm text-zinc-300 mb-4">{submitted.message}</div>
              <div className="text-[11px] font-mono text-zinc-500 mb-6">
                Reference ID: <span className="text-orange-400">{submitted.ticket_id}</span>
              </div>
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={() => setSubmitted(null)}
                  variant="outline"
                  data-testid="support-new-ticket"
                  className="border-zinc-700"
                >
                  Send another
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  data-testid="support-back-dashboard"
                  className="bg-orange-600 hover:bg-orange-500 text-zinc-950"
                >
                  Back to dashboard
                </Button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="support-form"
              className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-6 space-y-5"
            >
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-2 block">
                  Category
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {CATEGORIES.map((c) => {
                    const Icon = c.icon;
                    const active = category === c.key;
                    return (
                      <button
                        type="button"
                        key={c.key}
                        onClick={() => setCategory(c.key)}
                        data-testid={`support-cat-${c.key}`}
                        className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-sm border transition-colors ${
                          active
                            ? "border-orange-500 bg-orange-500/10"
                            : "border-zinc-800 hover:border-zinc-700"
                        }`}
                      >
                        <Icon size={18} weight={active ? "fill" : "regular"} className={active ? c.color : "text-zinc-500"} />
                        <span className={`text-[11px] font-mono uppercase tracking-wider ${active ? "text-zinc-100" : "text-zinc-400"}`}>
                          {c.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="support-name" className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
                    Your name
                  </Label>
                  <Input
                    id="support-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="support-name-input"
                    className="bg-zinc-900 border-zinc-700"
                  />
                </div>
                <div>
                  <Label htmlFor="support-email" className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
                    Reply to
                  </Label>
                  <Input
                    id="support-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="support-email-input"
                    className="bg-zinc-900 border-zinc-700"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="support-subject" className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
                  Subject
                </Label>
                <Input
                  id="support-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="support-subject-input"
                  maxLength={200}
                  placeholder="One-line summary of what's going on"
                  className="bg-zinc-900 border-zinc-700"
                  required
                />
              </div>

              <div>
                <Label htmlFor="support-message" className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1 block">
                  Message
                </Label>
                <textarea
                  id="support-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  data-testid="support-message-input"
                  rows={7}
                  maxLength={4000}
                  placeholder="What's happening? Steps to reproduce, error messages, whatever you've got. The more detail the better."
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-orange-500 focus:outline-none resize-y font-sans"
                  required
                />
                <div className="text-[10px] font-mono text-zinc-500 mt-1 text-right">
                  {message.length}/4000
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="support-submit"
                className="w-full bg-orange-600 hover:bg-orange-500 text-zinc-950 font-medium"
              >
                {loading ? (
                  <><Spinner size={14} className="animate-spin mr-2" /> Sending…</>
                ) : (
                  <><PaperPlaneTilt size={14} weight="fill" className="mr-2" /> Send to support</>
                )}
              </Button>
            </form>
          )}
        </div>

        {/* Right — Contact + FAQ */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-5">
            <div className="text-[10px] font-mono uppercase tracking-wider text-orange-500 mb-3">Direct contact</div>
            <div className="flex items-start gap-3">
              <Envelope size={16} weight="duotone" className="text-orange-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Email</div>
                <a
                  href="mailto:info@clickforgeds.com"
                  className="text-sm text-zinc-100 hover:text-orange-400 font-mono break-all"
                  data-testid="support-direct-email"
                >
                  info@clickforgeds.com
                </a>
                <div className="text-[11px] text-zinc-500 mt-1">Replies within 24 hours</div>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-5">
            <div className="text-[10px] font-mono uppercase tracking-wider text-orange-500 mb-3">Common questions</div>
            <div className="space-y-4">
              {FAQS.map((faq, i) => (
                <details key={i} className="group" data-testid={`support-faq-${i}`}>
                  <summary className="cursor-pointer text-sm text-zinc-200 hover:text-orange-400 list-none flex items-start gap-2">
                    <span className="text-orange-500 font-mono text-xs mt-0.5">▸</span>
                    <span className="flex-1">{faq.q}</span>
                  </summary>
                  <div className="mt-2 pl-5 text-[13px] text-zinc-400 leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
